import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import useDirectorsForm from "./useDirectorsForm";
import { Calendar } from "primereact/calendar";
import { useDispatch, useSelector } from "react-redux";
import {
  setRegData,
  handleAddNewPosition,
  handleRemovePosition,
  handleEditPositionField,
  handleSetPositionsData,
} from "./directorsRegFormSlice";
import { RadioButton } from "primereact/radiobutton";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";

const DirectorsRegistration = ({
  btnToggle,
  setBtnToggle,
  formDetails,
  setFormDetails,
  formUpdate,
  setFormUpdate,
  validationMsg,
  errorIndex,
}) => {
  const { endDate, setEndDate, setSecondEndDate, positions, roles } =
    useDirectorsForm();

  const status = [
    {
      name: (
        <>
          <span className="pi pi-circle-fill active-circle-fill"></span> Active
        </>
      ),
      code: "Active",
    },
    {
      name: (
        <>
          <span className="pi pi-circle-fill not-active-circle-fill"></span> Not
          Active
        </>
      ),
      code: "Not Active",
    },
    {
      name: (
        <>
          <span className="pi pi-circle-fill removed-circle-fill"></span>{" "}
          Removed
        </>
      ),
      code: "Removed",
    },
  ];

  const dispatch = useDispatch();
  const regData = useSelector((state) => state.directorsRegForm.regData || {});
  const positionsData = useSelector((state) => state.directorsRegForm.regData);
  useEffect(() => {
    dispatch(handleSetPositionsData(regData.positions));
  }, [regData.positions]);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: regData.name || "",
      other_name: regData.other_name || "",
      last_name: regData.last_name || "",
      preferred_name: regData.preferred_name || "",
      email: regData.email || "",
      phone_code: regData.phone_code || "",
      phone: regData.phone || "",
      position: regData.position || "",
      start_date: regData.start_date || "",
      end_date: regData.end_date || "",
      status: regData.status || "",
      role: regData.role || "",
      title: regData.title || "",
    },
  });
  useEffect(() => {
    if (formDetails) {
      reset({
        name: formDetails.name || "",
        other_name: formDetails.other_name || "",
        last_name: formDetails.last_name || "",
        preferred_name: formDetails.preferred_name || "",
        email: formDetails.email || "",
        phone_code: formDetails.phone_code || "",
        phone: formDetails.phone || "",
        position: formDetails.position || "",
        start_date: formDetails.start_date || "",
        end_date: formDetails.end_date || "",
        status: formDetails.status || "",
        role: formDetails.role || "",
        title: formDetails.title || "",
      });
    }
  }, [formDetails, reset]);

  const onSubmit = (data) => {
    setFormDetails(data);
    dispatch(setRegData(data));
  };

  const handleShowInput = () => {
    dispatch(handleAddNewPosition());
  };

  useEffect(() => {
    let isValid = false;

    if (regData) {
      const {
        name,
        email,
        position,
        status,
        positions,
        start_date,
        preferred_name,
        other_name,
        last_name,
        role,
      } = regData;

      if (
        !/^[A-Za-z\s]+$/.test(name?.trim()) ||
        !/\S+@\S+\.\S+/.test(email?.trim()) ||
        !position?.trim() ||
        !status?.trim() ||
        !start_date ||
        errors.phone ||
        errors.phone_code ||
        (preferred_name?.length > 0 &&
          !/^[A-Za-z\s]+$/.test(preferred_name?.trim())) ||
        (other_name?.length > 0 && !/^[A-Za-z\s]+$/.test(other_name?.trim())) ||
        (last_name?.length > 0 && !/^[A-Za-z\s]+$/.test(last_name?.trim())) ||
        !role.length
      ) {
        isValid = true;
      }

      if (positions) {
        for (const item of positions) {
          if (
            !item.position?.trim() ||
            !item.status?.trim() ||
            !item.start_date
          ) {
            isValid = true;
            break;
          }
        }
      }
    }

    setBtnToggle(isValid);
  }, [regData, errors.phone, errors.phone_code]);

  const roleOptionsTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            typeof regData?.role === "object"
              ? regData?.role.code === option.code
              : regData?.role === option.code
          }
        />
        <span>{option.name}</span>
      </div>
    );
  };
  return (
    <>
      <div className="formgrid grid">
        <div className="field col-12">
          <p className="add-new-entity-heading mb-2">Appointment Details</p>
          <p className="input-label m-0">
            Enter all the details for this appointment
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="formgrid grid">
          <div className="field col-6">
            <label className="contact-label mt-2">
              Given name(s) *{" "}
              <span id="given-name-info" className="pi pi-info-circle"></span>
              <Tooltip
                target="#given-name-info"
                content="This is usually the person's first name or the name they usually go by."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="name"
              control={control}
              defaultValues
              rules={{
                required: "Given Name is required.",
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.name || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ name: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter given name(s)"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.name && (
              <small className="error-message">{errors.name.message}</small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Other Name(s)
              <span
                id="other-name-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#other-name-info"
                content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="other_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.other_name || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ other_name: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter any other names, such as middle names or clan names"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.other_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.other_name && (
              <small className="error-message">
                {errors.other_name.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Surname(s)
              <span
                id="surname-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#surname-info"
                content="This is usually a family name or last name. If employee has more than one, include them here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="last_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.last_name || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ last_name: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter surname(s)."
                  className={`input-field appearance-none outline-none w-full ${
                    errors.last_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.last_name && (
              <small className="error-message">
                {errors.last_name.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Preferred Name(s)
              <span
                id="preferred-name-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#preferred-name-info"
                content="If the employee wants to be called by a different name than their given name, put that here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="preferred_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.preferred_name || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ preferred_name: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Preferred name or nickname"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.preferred_name ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.preferred_name && (
              <small className="error-message">
                {errors.preferred_name.message}
              </small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Role *</label>
            <Controller
              name="role"
              control={control}
              rules={{ required: "Role is required." }}
              render={({ field }) => {
                const roleObject =
                  typeof regData?.role === "object"
                    ? regData?.role
                    : roles?.find((role) => role.name === regData?.role);
                return (
                  <Dropdown
                    {...field}
                    value={roleObject}
                    onChange={(e) => {
                      const newValue = e.value;
                      const updatedRole = {
                        name: newValue.name,
                        code: newValue.code,
                      };
                      dispatch(setRegData({ role: updatedRole.name }));
                      field.onChange(updatedRole);
                    }}
                    options={
                      roleObject && !roles.length
                        ? [roleObject]
                        : roles.length
                          ? roles
                          : [roleObject].filter(Boolean)
                    }
                    optionLabel="name"
                    optionValue="code"
                    itemTemplate={roleOptionsTemplate}
                    placeholder="Select role"
                    className={`w-full ${errors.role ? "error" : ""}`}
                  />
                );
              }}
            />
            {errors.role && (
              <small className="error-message">{errors.role.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Title</label>
            <Controller
              name="title"
              control={control}
              defaultValues
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.title || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ title: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter title"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.title ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.title && (
              <small className="error-message">{errors.title.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Contact email*</label>
            <Controller
              name="email"
              control={control}
              defaultValues
              rules={{
                required: "Email Address is required.",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.email || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ email: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.email ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.email && (
              <small className="error-message">{errors.email.message}</small>
            )}
          </div>
          <div className="field col-12 mb-0">
            <label className="contact-label mt-2">Phone Number</label>
          </div>
          <div className="field col-3">
            <Controller
              name="phone_code"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone code must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.phone_code || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ phone_code: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Country Code"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone_code ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.phone_code && (
              <small className="error-message">
                {errors.phone_code.message}
              </small>
            )}
          </div>
          <div className="field col-9">
            <Controller
              name="phone"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone number must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={regData?.phone || ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(setRegData({ phone: newValue }));
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter phone number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone ? "error" : ""
                  }`}
                />
              )}
            />
            {errors.phone && (
              <small className="error-message">{errors.phone.message}</small>
            )}
          </div>
          <div className="field col-12">
            <p className="current-position-head mt-2">Current Position</p>
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">
              Choose Directors position *
            </label>
            <Controller
              name="selectedPosition"
              control={control}
              rules={{ required: "Director's position is required." }}
              render={({ field }) => {
                const currentPosition = regData?.position;

                const positionObject = currentPosition
                  ? { name: currentPosition, code: currentPosition }
                  : null;
                return (
                  <Dropdown
                    {...field}
                    value={positionObject}
                    onChange={(e) => {
                      const newValue = e.value;
                      const updatedPosition = newValue.code;
                      dispatch(setRegData({ position: updatedPosition }));

                      field.onChange(updatedPosition);
                    }}
                    options={
                      positionObject && !positions.length
                        ? [positionObject]
                        : positions.length
                          ? positions
                          : [positionObject].filter(Boolean)
                    }
                    optionLabel="name"
                    optionValue="code"
                    placeholder="Select position"
                    className={`w-full ${errors.selectedPosition ? "error" : ""}`}
                  />
                );
              }}
            />
            {errors.selectedPosition && (
              <small className="error-message">
                {errors.selectedPosition.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">Appointment Date*</label>
            <div className="calendar-container">
              <Controller
                name="appointmentDate"
                control={control}
                rules={{
                  required: "Appointment date is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Calendar
                      {...field}
                      value={
                        removeTimezoneFromDateString(field.value) ||
                        removeTimezoneFromDateString(regData.start_date)
                      }
                      onChange={(e) => {
                        const newDate = formatDateToISOStartOfDay(e.value);
                        dispatch(setRegData({ start_date: newDate }));
                        field.onChange(newDate);
                      }}
                      placeholder="Select start date"
                      className="w-full"
                    />
                    <span className="pi pi-calendar calendar-icon"></span>
                    {!regData.start_date && error && (
                      <small className="p-error">{error.message}</small>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">End Date</label>
            <div className="calendar-container">
              <Calendar
                value={
                  removeTimezoneFromDateString(endDate) ||
                  removeTimezoneFromDateString(regData.end_date)
                }
                onChange={(e) => {
                  const newEndDate = formatDateToISOStartOfDay(e.value);
                  setEndDate(newEndDate);
                  dispatch(setRegData({ end_date: newEndDate }));
                }}
                minDate={
                  regData.start_date
                    ? new Date(
                        new Date(regData.start_date).setDate(
                          new Date(regData.start_date).getDate() + 1,
                        ),
                      )
                    : null
                }
                disabled={!regData.start_date}
                placeholder="Select end date"
                className="w-full"
              />
              <span className="pi pi-calendar calendar-icon"></span>
            </div>
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Status*</label>
            <Controller
              name="selectedStatus"
              control={control}
              rules={{ required: "Status is required." }}
              render={({ field }) => {
                const currentStatus = regData?.status;
                const statusObject = currentStatus
                  ? status.find((s) => s.code === currentStatus)
                  : null;

                return (
                  <Dropdown
                    {...field}
                    value={statusObject || regData.status}
                    onChange={(e) => {
                      const newValue = e.value;
                      const updatedStatus = newValue.code;
                      dispatch(setRegData({ status: updatedStatus }));

                      field.onChange(updatedStatus);
                    }}
                    options={status}
                    optionLabel="name"
                    optionValue="code"
                    placeholder="Select status"
                    itemTemplate={(item) => item.name}
                    className={`w-full ${errors.selectedStatus ? "error" : ""}`}
                  />
                );
              }}
            />
            {errors.selectedStatus && (
              <small className="error-message">
                {errors.selectedStatus.message}
              </small>
            )}
          </div>
          {formUpdate ? (
            <>
              {positionsData.positions &&
                positionsData.positions.map((data, index) => {
                  return (
                    <>
                      <div className="field col-12">
                        <div className="remove-position">
                          <p className="new-position mt-2"> New Position </p>
                          <button
                            className="generic-cancel"
                            onClick={() => {
                              dispatch(handleRemovePosition(index));
                            }}
                          >
                            Remove Position
                          </button>
                        </div>
                      </div>
                      <div className="field col-12">
                        <label className="contact-label mt-2">
                          Choose Director's position *
                        </label>
                        <Controller
                          name={`positions[${index}].position`}
                          control={control}
                          rules={{
                            required: "Director's position is required.",
                          }}
                          render={({ field }) => {
                            const secondaryPosition = data.position;
                            const positionObject = secondaryPosition
                              ? {
                                  name: secondaryPosition,
                                  code: secondaryPosition,
                                }
                              : null;
                            return (
                              <Dropdown
                                {...field}
                                value={positionObject}
                                onChange={(e) => {
                                  const newValue = e.value;
                                  const updatedPosition = newValue.code;
                                  field.onChange(updatedPosition);
                                  dispatch(
                                    handleEditPositionField({
                                      field: "position",
                                      value: updatedPosition,
                                      index,
                                    }),
                                  );
                                }}
                                options={positions}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select position"
                                className={`w-full ${
                                  errors?.positions?.[index]?.position
                                    ? "error"
                                    : ""
                                }`}
                              />
                            );
                          }}
                        />
                        {errors.secondaryPosition && (
                          <small className="error-message">
                            {errors.positions[index].position.message}
                          </small>
                        )}
                        {errorIndex.includes(index + 1) && (
                          <small className="error-message">
                            {validationMsg[index + 1]}
                          </small>
                        )}
                      </div>
                      <div className="field col-6">
                        <label className="contact-label mt-2">
                          Appointment Date*
                        </label>
                        <div className="calendar-container">
                          <Controller
                            name="secondaryAppointmentDate"
                            control={control}
                            rules={{
                              required: "Appointment date is required.",
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Calendar
                                  {...field}
                                  value={
                                    data.start_date
                                      ? removeTimezoneFromDateString(
                                          data.start_date,
                                        )
                                      : null
                                  }
                                  onChange={(e) => {
                                    const newDate = formatDateToISOStartOfDay(
                                      e.value,
                                    );
                                    field.onChange(newDate);
                                    dispatch(
                                      handleEditPositionField({
                                        field: "start_date",
                                        value: newDate,
                                        index,
                                      }),
                                    );
                                  }}
                                  placeholder="Select start date"
                                  className="w-full"
                                />
                                <span className="pi pi-calendar calendar-icon"></span>
                                {error && (
                                  <small className="p-error">
                                    {error.message}
                                  </small>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </div>
                      <div className="field col-6">
                        <label className="contact-label mt-2">End Date</label>
                        <div className="calendar-container">
                          <Calendar
                            value={
                              data.end_date
                                ? removeTimezoneFromDateString(data.end_date)
                                : null
                            }
                            onChange={(e) => {
                              const newEndDate = formatDateToISOStartOfDay(
                                e.value,
                              );
                              setSecondEndDate(newEndDate);
                              dispatch(
                                handleEditPositionField({
                                  field: "end_date",
                                  value: newEndDate,
                                  index,
                                }),
                              );
                            }}
                            minDate={
                              data.start_date
                                ? new Date(
                                    new Date(data.start_date).setDate(
                                      new Date(data.start_date).getDate() + 1,
                                    ),
                                  )
                                : null
                            }
                            disabled={!data.start_date}
                            placeholder="Select end date"
                            className="w-full"
                          />
                          <span className="pi pi-calendar calendar-icon"></span>
                        </div>
                      </div>
                      <div className="field col-12">
                        <label className="contact-label mt-2">Status*</label>
                        <Controller
                          name={`positions[${index}].status`}
                          control={control}
                          rules={{ required: "Status is required." }}
                          render={({ field }) => {
                            const currentStatus = data.status;
                            const statusObject = currentStatus
                              ? status.find((s) => s.code === currentStatus)
                              : null;
                            return (
                              <Dropdown
                                {...field}
                                value={statusObject}
                                onChange={(e) => {
                                  const newValue = e.value;
                                  const updatedStatus = newValue.code;
                                  field.onChange(updatedStatus);
                                  dispatch(
                                    handleEditPositionField({
                                      field: "status",
                                      value: updatedStatus,
                                      index,
                                    }),
                                  );
                                }}
                                options={status}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select status"
                                className={`w-full ${
                                  errors?.positions?.[index]?.status
                                    ? "error"
                                    : ""
                                }`}
                              />
                            );
                          }}
                        />
                        {errors.secondaryStatus && (
                          <small className="error-message">
                            {errors.positions[index].status.message}
                          </small>
                        )}
                      </div>
                    </>
                  );
                })}
              <div className="field col-12">
                <div className="remove-position">
                  <button
                    type="button"
                    className="cancel-btns"
                    onClick={handleShowInput}
                  >
                    Add New Position <span className="pi pi-plus ml-2"></span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            ""
          )}
        </div>
      </form>
    </>
  );
};

export default DirectorsRegistration;
